import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { HookFormText } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { useCustomForms } from '../../../../hooks/useCustomForms/useCustomForms';

import styles from './modals.module.scss';

type Props = {
  onClose: () => void;
};

type FormValues = {
  label: string;
  submitButtonLabel: string;
  submitSuccessMessage: string;
};

const maxNameLength = 256;

export function AddFormModal({ onClose }: Props): JSX.Element {
  const customForms = useCustomForms();
  const navigate = useAppNavigate();
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      submitButtonLabel: 'Send',
      submitSuccessMessage: 'Message sent',
    },
  });
  const notify = useNotifications();

  const submitHandler = handleSubmit(async (formValues) => {
    const result = await customForms.createForm({
      ...formValues,
      description: '', // Description is not a part of the current UI design, but required by the API
    });

    if (result.ok) {
      notify({
        title: 'Form created',
        time: 5000,
        icon: 'done',
        className: 'success',
      });
      handleOnClose();
      navigate({
        path: '/settings/custom-forms/edit/:formId',
        params: { path: { formId: result.value.id } },
      });
      return;
    }

    if (result.error) {
      notify({
        title: 'Could not create form',
        text: result.error,
        className: 'danger',
        time: 5000,
        icon: 'exclamation',
      });
    }
  });

  const handleOnClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Modal className={styles.root} isOpen onClose={onClose}>
      <ModalHead title="Add new form" onClose={handleOnClose} />
      <form onSubmit={submitHandler}>
        <ModalBody>
          <HookFormText
            name="label"
            label="Form name"
            control={control}
            disabled={formState.isSubmitting}
            rules={{
              required: { value: true, message: 'Name is required' },
              maxLength: {
                value: maxNameLength,
                message: `Must not exceed ${maxNameLength} characters`,
              },
            }}
            componentProps={{
              id: 'label',
              placeholder: 'Name your form',
              maxLength: maxNameLength,
              autoFocus: true,
            }}
          />
          <HookFormText
            name="submitButtonLabel"
            label="Form submission button label"
            control={control}
            disabled={formState.isSubmitting}
            rules={{
              required: {
                value: true,
                message: 'Form submission button label is required',
              },
              maxLength: {
                value: maxNameLength,
                message: `Must not exceed ${maxNameLength} characters`,
              },
            }}
            componentProps={{
              id: 'label',
              maxLength: maxNameLength,
            }}
          />
          <HookFormText
            name="submitSuccessMessage"
            label="Feedback after form submission"
            control={control}
            disabled={formState.isSubmitting}
            rules={{
              required: {
                value: true,
                message: 'Feedback after form submission is required',
              },
              maxLength: {
                value: maxNameLength,
                message: `Must not exceed ${maxNameLength} characters`,
              },
            }}
            componentProps={{
              id: 'label',
              maxLength: maxNameLength,
            }}
          />
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleOnClose}
          cancelProps={{ disabled: formState.isSubmitting }}
          confirmName="Add form"
          confirmOnClick={submitHandler}
          confirmProps={{
            type: 'submit',
            isLoading: formState.isSubmitting,
            loadingIndicator: 'Adding form...',
          }}
        />
      </form>
    </Modal>
  );
}
